import { AxiosError } from 'axios'
import { ErrorBox } from '@outbound-ai/ursa'
import { LoadingBox } from '@outbound-ai/ursa'
import { CallInfoGroupDetails } from 'features/calls/components'
import { CallInfoKindEnum, IProperty } from 'features/calls/types'
import { claimEntitiesDetails } from 'features/claims/claimsModel'
import { claimDetailsType, IClaim } from 'features/claims/types'
import { useEntities } from 'features/entities/entityHooks'
import { keys } from 'lodash'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { mapEntities } from 'utils/dataExtractor'
import { formatDateOnly } from 'utils/date'
import { errorHandler } from 'utils/errors/errors'

interface IProps {
  claim: IClaim
}

const CallInfo = ({ claim }: IProps) => {
  const [slotData, setSlotData] = useState<any>({})
  const {
    data: allEntities,
    error: entitiesError,
    isLoading: isLoadingEntities,
  } = useEntities({
    onError: (err: AxiosError) => errorHandler.handleError(err, ErrorMessagesEnum.EntitiesError),
  })

  // variables
  const error = entitiesError
  const hasData = allEntities && !error
  const isLoading = isLoadingEntities

  useEffect(() => {
    const mappedSlotData = mapEntities((allEntities as any) || [], claim || {})
    setSlotData(mappedSlotData)
  }, [claim, allEntities])

  return (
    <Section>
      <section className="header">
        <h3>Claim Summary</h3>
      </section>
      <section className="body">
        {error && <ErrorBox {...errorHandler.errorToErrorBoxProps(error)} />}

        {isLoading && <LoadingBox message="Loading info..." />}

        {hasData &&
          keys(claimEntitiesDetails).map((entityGroupName: string) => {
            const groupRows = claimEntitiesDetails[entityGroupName as claimDetailsType]
              .map((entityId: string) => {
                if (!slotData) return null
                const entity = Array.isArray(slotData) ? slotData.find((e: any) => e.slot === entityId) : null
                if (!entity) return null
                const value = entity.typeId === 'dateonly' ? formatDateOnly(entity.value) : entity.value || '---'
                const friendlyName = entity.description || '---'
                const name = entity.description || '---'
                if (value != '---') {
                  return {
                    name: name,
                    isHidden: false,
                    friendlyName: friendlyName,
                    kind: CallInfoKindEnum.string,
                    value: value,
                    valueInit: value,
                  } as IProperty
                }
              })
              .filter(Boolean) as IProperty[]
            return (
              <CallInfoGroupDetails
                key={entityGroupName}
                title={entityGroupName}
                rows={groupRows}
                callState={'closed'}
              />
            )
          })}
      </section>
    </Section>
  )
}

const Section = styled.section`
  height: 100%;

  .header {
    height: ${({ theme }) => theme.constants.navbarHeight};
    width: 100%;
    display: flex;
    justify-content: start;
    align-items: center;
    padding-left: ${({ theme }) => theme.util.pixelToRem(34)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    h3 {
      font-family: 'Milliard';
      font-style: normal;
      font-weight: 600;
      font-size: 1.25rem;
      line-height: 150%;
      color: ${({ theme }) => theme.colors.primary.darker};
    }
  }

  .body {
    height: 100%;
    position: relative;
  }
`

export { CallInfo }
